import React, { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useClickOutside from "../../hooks/useClickOutside";
import { useAuth } from "../../context/AuthContext";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignUpClick: () => void;
}

interface LoginForm {
  email: string;
  password: string;
}

const LoginModal: FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSignUpClick,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginForm>({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, socialLogin } = useAuth();

  useClickOutside(modalRef, onClose);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(formData.email, formData.password);
      onClose();
      navigate("/courses");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setError("");
    setIsLoading(true);
    
    try {
      await socialLogin(provider);
      // Close modal after successful login
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${provider} login failed`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div ref={modalRef} className="bg-white rounded-2xl p-8 w-full max-w-md">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center bg-white rounded-full shadow-lg text-gray-400 hover:text-gray-600"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="flex flex-col items-center mb-8">
          <h2 className="text-2xl font-bold mb-2">Welcome Back!</h2>
          <p className="text-gray-500 text-sm">Sign in to continue learning</p>
        </div>

        {/* Social Login Buttons */}
        <div className="flex gap-4 mb-8">
          <button 
            onClick={() => handleSocialLogin('google')}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 border rounded-lg py-2.5 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <img src="/images/google.png" alt="Google" className="w-5 h-5" />
            <span className="text-sm">Google</span>
          </button>
          <button 
            onClick={() => handleSocialLogin('facebook')}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 border rounded-lg py-2.5 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <img src="/images/facebook.png" alt="Facebook" className="w-5 h-5" />
            <span className="text-sm">Facebook</span>
          </button>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm mb-1.5">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1.5">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className="w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2">
              <input type="checkbox" className="rounded text-[#55B7FF]" />
              <span className="text-sm">Remember me</span>
            </label>
            <button
              type="button"
              className="text-sm text-[#55B7FF] hover:underline"
            >
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#55B7FF] text-white py-2.5 rounded-lg hover:bg-[#0284C7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="text-center mt-6 text-sm text-gray-500">
          Don't have an account?{" "}
          <button
            onClick={() => {
              onClose();
              onSignUpClick();
            }}
            className="text-[#55B7FF] hover:underline"
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
